// src/components/OrderDialog.jsx
import printJS from 'print-js';
import { FaFilePdf, FaPrint, FaTimes } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { formatDate } from '../utils/formatDate';
import { formatDateTime } from '../utils/formatDateTime';
import html2pdf from 'html2pdf.js';
import VerifyDialog from '../utils/VerifyDialog';
import axiosInstance from '../utils/axiosInstance';
import CupcakeOrderDetailsDisplay from './CupcakeOrderDetailsDisplay';
import { OrderStatusPanel } from './StatusControlPanel';

const OrderDialog = ({ order, onClose }) => {
  const [shopNotes, setShopNotes] = useState('');
  const [includeNotesInPrint, setIncludeNotesInPrint] = useState(false);
  const [includeDayColorInPrint, setIncludeDayColorInPrint] = useState(false);
  // extra toggles for invoice printout
  const [includeEmailMessageInPrint, setIncludeEmailMessageInPrint] = useState(false);
  const [includeAdditionalNotesInPrint, setIncludeAdditionalNotesInPrint] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);


  // data pulled from quote + quote request
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [quoteRequestDetails, setQuoteRequestDetails] = useState(null);
  const [quoteOptions, setQuoteOptions] = useState({});



  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Files attached on the originating quote request (if any)
  const [quoteFiles, setQuoteFiles] = useState([]);

  // Pricing breakdown from the accepted quote (if available)
  const [quotePrices, setQuotePrices] = useState(null);

  // Add Sunday and map 0..6 correctly
  const dayColorMap = {
    1: { name: 'Mon', color: '#FF6B6B' },
    2: { name: 'Tue', color: '#FFD93D' },
    3: { name: 'Wed', color: '#6BCB77' },
    4: { name: 'Thu', color: '#4D96FF' },
    5: { name: 'Fri', color: '#9D4EDD' },
    6: { name: 'Sat', color: '#FF922B' },
  };
  const pickupDate = new Date(order.eventDate);
  const pickupDayOfWeek = pickupDate.getDay() + 1; // 0..6
  const dayInfo = dayColorMap[pickupDayOfWeek];

  const fmtUSD = (n) => `$${Number(n ?? 0).toFixed(2)}`;
  const canDelete = ['Rejected', 'Cancelled'].includes(order.status);
  const isQuoteOrder = Boolean(order.quoteRequest); // linked to QuoteRequest
  const isCupcakeOrder = order.cakeShape === 'Cupcakes';

  useEffect(() => {
    if (order) setShopNotes(order.shopNotes || '');
  }, [order]);

  // Debounced auto-save of shop notes
  useEffect(() => {
    if (!order) return;
    const t = setTimeout(() => {
      if (shopNotes !== order.shopNotes) handleSaveShopNotes(shopNotes);
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopNotes, order?._id]);

  // Fetch attachments from originating quote
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!order?.quoteRequest) return;
        const { data } = await axiosInstance.get(`/api/quote-requests/${order.quoteRequest}/files-urls`);
        if (!active) return;
        setQuoteFiles(Array.isArray(data?.files) ? data.files : []);
      } catch {
        if (active) setQuoteFiles([]);
      }
    })();
    return () => { active = false; };
  }, [order?.quoteRequest]);

  const [quotesList, setQuotesList] = useState([]);

  useEffect(() => {
    if (!order.quoteRequest) return;
    axiosInstance
      .get(`/api/quote-requests/${order.quoteRequest}/quotes`)
      .then(res => setQuotesList(res.data?.quotes || []))
      .catch(() => { });
  }, [order.quoteRequest]);


  // Fetch accepted quote to show pricing breakdown
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!order?.quoteRequest) return;
        const { data } = await axiosInstance.get(`/api/quote-requests/${order.quoteRequest}/quotes`);
        const list = Array.isArray(data?.quotes) ? data.quotes : [];
        // Prefer exact quoteId match; fallback to status=accepted; fallback to latest
        let chosen =
          (order.quoteId && list.find((q) => q.quoteId === order.quoteId)) ||
          list.find((q) => (q.status || '').toLowerCase() === 'accepted') ||
          list[0];


        if (!chosen) return;
        const p = chosen.prices || {};
        if (!active) return;
        setQuotePrices({
          cake: Number(p.cake || 0),
          decoration: Number(p.decoration || 0),
          filling: Number(p.filling || 0),
          flavor: Number(p.flavor || 0),
        });
        setQuoteDetails(chosen || null);
        setQuoteOptions(chosen?.options || {});

      } catch {
        if (active) setQuotePrices(null);
      }
    })();
    return () => { active = false; };
  }, [order?.quoteRequest, order?.quoteId]);

  // Fetch Quote Request details (for description)
  useEffect(() => {
    if (!order?.quoteRequest) return;
    let active = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/api/quote-requests/${order.quoteRequest}`);
        if (!active) return;
        // data shape in your controller is { success, quote }
        setQuoteRequestDetails(data?.quote || null);
      } catch {
        if (active) setQuoteRequestDetails(null);
      }
    })();
    return () => { active = false; };
  }, [order?.quoteRequest]);


  const handlePrint = () => {
    printJS({ printable: 'invoice', type: 'html', targetStyles: ['*'] });
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('invoice');
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Order_${order.orderId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 1.5, useCORS: true, scrollY: 0, scrollX: 0 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all' }
    };
    setTimeout(() => html2pdf().set(opt).from(element).save(), 100);
  };

  const handleAcceptClick = () => { setActionType('accept'); setRejectionReason(''); setVerifyOpen(true); };
  const handleRejectClick = () => { setActionType('reject'); setVerifyOpen(true); };
  const handleDeleteClick = () => { setActionType('delete'); setRejectionReason(''); setVerifyOpen(true); };

  const handleVerifyConfirm = async () => {
    try {
      if (actionType === 'accept') {
        await axiosInstance.put(`/api/orders/${order._id}/accept`);
      } else if (actionType === 'reject') {
        await axiosInstance.put(`/api/orders/${order._id}/reject`, {
          rejectionReason: rejectionReason.trim(),
        });
      } else if (actionType === 'delete') {
        if (!canDelete) {
          alert('Only Rejected or Cancelled orders can be deleted.');
          setVerifyOpen(false);
          return;
        }
        await actuallyDeleteOrder();
        return; // closed + reloaded already
      }
      setVerifyOpen(false);
      onClose();
      window.location.reload();
    } catch (error) {
      console.error(`❌ Failed to ${actionType} order:`, error);
      alert(`Failed to ${actionType} order.`);
    }
  };
  const handleVerifyCancel = () => {
    setVerifyOpen(false);
    if (actionType !== 'reject') setRejectionReason('');
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      setStatusBusy(true);
      await axiosInstance.put(`/api/orders/${order._id}/update-status`, { status: newStatus });
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to update status:', error);
      alert('Failed to update order status.');
    } finally {
      setStatusBusy(false);
    }
  };

  const handleSaveShopNotes = async (text) => {
    try {
      setSaving(true);
      const { data: updated } = await axiosInstance.put(`/api/orders/shop-notes/${order._id}`, { shopNotes: text });
      order.shopNotes = updated.shopNotes;
      setSavedMessage('Saved ✓');
      setTimeout(() => setSavedMessage(''), 5000);
    } catch (err) {
      console.error('❌ Auto-save failed:', err);
      setSavedMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const actuallyDeleteOrder = async () => {
    try {
      await axiosInstance.delete(`/api/orders/${order._id}`);
      onClose();
      window.location.reload();
    } catch (err) {
      console.error('Failed to delete order:', err);
      alert('Failed to delete order');
    }
  };

  if (!order) return null;

  // Pull topper data if stored by accept flow (gracefully optional)
  const topperDisplay = order.topper || order.cakeTopper || '';
  const topperPrice = Number(order.topper_price || 0);

  const ChoiceIcon = ({ by }) => {
    const isCustomer = by === "customer";
    return (
      <span className="inline-flex items-center relative group ml-1 cursor-default print:cursor-auto">
        {isCustomer ? (
          <svg xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-green-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 0114 0H3z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-blue-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M2 10V6l8-4 8 4v4h-1v8H3v-8H2zm3 0h10v6H5v-6z" />
          </svg>
        )}

        {/* Tooltip (hidden in PDF/print) */}
        <span className="
        absolute bottom-6 left-1/2 -translate-x-1/2
        bg-black text-white text-[10px] px-2 py-[2px]
        rounded whitespace-nowrap
        opacity-0 group-hover:opacity-100 transition
        pointer-events-none
        print:hidden
      ">
          {isCustomer ? "Chosen by customer" : "Chosen by store"}
        </span>
      </span>
    );
  };



  const cakeDescription = quoteRequestDetails?.description || null;
  const emailMessage = quoteDetails?.note || null;
  const additionalNotes = quoteDetails?.cakeData?.additional || null;
  const cupcakeDozensDisplay = Number(order.cupcakeDozens || 0) > 0
    ? Number(order.cupcakeDozens)
    : (Number(order.servings || 0) > 0 ? Number(order.servings) / 12 : 0);
  const sprinkleDisplay = Array.isArray(order.sugarSprinkleColors)
    ? order.sugarSprinkleColors.join(' + ')
    : '';

  const customerCanChooseFrosting = !!quoteOptions.customerCanChooseFrosting;
  const customerCanSeeAdditional = !!quoteOptions.customerCanSeeAdditional;
  const customerCanChooseTopper = !!quoteOptions.customerCanChooseTopper;



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl mx-2 sm:mx-4 md:mx-auto p-4 sm:p-6 md:p-8 rounded-lg shadow-lg overflow-y-auto overscroll-y-contain max-h-[90dvh] relative print:w-full print:max-w-full print:p-8 print:shadow-none print:rounded-none print:overflow-visible"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 print:hidden">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Order Details</h2>
          <div className="flex flex-wrap gap-2">
            <button className="flex items-center gap-2 bg-red-100 text-red-600 px-3 py-2 text-sm rounded hover:bg-red-200" onClick={handleDownloadPDF}><FaFilePdf /> Download Invoice</button>
            <button className="flex items-center gap-2 bg-blue-100 text-blue-600 px-3 py-2 text-sm rounded hover:bg-blue-200" onClick={handlePrint}><FaPrint /> Print</button>
            <button className="flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-2 text-sm rounded hover:bg-gray-200" onClick={onClose}><FaTimes /> Close</button>
          </div>
        </div>

        {/* Printable Invoice */}
        <div id="invoice" className="text-black text-sm font-sans leading-relaxed w-full max-w-[794px] mx-auto mt-4 sm:mt-10 p-3 sm:p-6 space-y-6">
          {/* Header block */}
          <div className="border-b pb-4">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-candy-primary">Candy Haven and Cakes</h1>
                <p className="text-xs text-gray-700">www.candyhavenandcakes.com</p>
                {(order.quoteId || order.quoteRequestId) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {order.quoteId && <>Quote: <span className="font-medium">{order.quoteId}</span></>}
                    {order.quoteId && order.quoteRequestId && ' • '}
                    {order.quoteRequestId && <>Request: <span className="font-medium">{order.quoteRequestId}</span></>}
                  </p>
                )}
              </div>
              <div className="flex justify-end items-center space-x-4">
                <div className="text-right w-40">
                  <h2 className="text-xl font-semibold uppercase">Invoice</h2>
                  <p><strong>Order ID:</strong> {order.orderId}</p>
                  <p><strong>Date:</strong> {formatDate(order.eventDate)}</p>
                  <p><strong>Time: </strong>{order.eventTime || '—'}</p>
                </div>
                {dayInfo && !includeDayColorInPrint && (
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded text-white font-bold text-xs"
                    style={{ backgroundColor: dayInfo.color }}
                  >
                    {dayInfo.name}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Customer Information</h3>
            <p><strong>Name:</strong> {order.firstName} {order.lastName}</p>
            <p><strong>Email:</strong> {order.customerEmail}</p>
            <p><strong>Phone:</strong> {order.customerPhone}</p>
            <p><strong>Ordered on :</strong> {formatDateTime(new Date(new Date(order.createdAt).setHours(new Date(order.createdAt).getHours())))}</p>

          </div>

          {/* Cake/Cupcake Details */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">
              {isCupcakeOrder ? 'Cupcake Details' : 'Cake Details'}
            </h3>
            {isCupcakeOrder ? (
              <div className="mt-3 rounded-xl border border-candy-border bg-gray-50/80 p-3 sm:p-4">
                {(order.cupcakeUnitConfigs?.length || 0) > 0 ? (
                  <CupcakeOrderDetailsDisplay
                    variant="admin"
                    cupcakeDozens={order.cupcakeDozens}
                    cupcakeUnitConfigs={order.cupcakeUnitConfigs}
                    cupcakeConfigMode={order.cupcakeConfigMode}
                  />
                ) : (
                  <div className="space-y-2 text-sm sm:text-base">
                    <p><strong>Quantity:</strong> {cupcakeDozensDisplay || 0} dozen ({Math.round((cupcakeDozensDisplay || 0) * 12)} cupcakes)</p>
                    <p><strong>Flavor:</strong> {order.cakeFlavor || '—'}</p>
                    <p><strong>Icing:</strong> {order.frosting || '—'}</p>
                    <p><strong>Sugar Sprinkles:</strong> {sprinkleDisplay || '—'}</p>
                  </div>
                )}
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-2">
              <p><strong>Shape:</strong> {order.cakeShape || '—'}</p>
              <p><strong>Size:</strong> {order.cakeSize || '—'}</p>
              <p><strong>Flavor:</strong> {order.cakeFlavor || '—'}</p>
              <p><strong>Filling:</strong> {order.fillingFlavor || '—'}</p>
              <div className="flex items-center gap-2">
                <strong>Frosting:</strong>
                <span className="flex items-center gap-1">
                  {order.frosting || '—'}
                  {isQuoteOrder && (
                    customerCanChooseFrosting
                      ? <ChoiceIcon by="customer" />
                      : <ChoiceIcon by="store" />
                  )}
                </span>
              </div>
              <p className="flex items-center gap-1">
                <strong>Topper:</strong>
                <span className="inline-flex items-center gap-1">
                  {topperDisplay || '—'}
                  {isQuoteOrder && (
                    customerCanChooseTopper
                      ? <ChoiceIcon by="customer" />
                      : <ChoiceIcon by="store" />
                  )}
                </span>
              </p>
              {order.decorationStyle ? (
                <p className="sm:col-span-2"><strong>Decoration:</strong> {order.decorationStyle}</p>
              ) : null}
              {isQuoteOrder && cakeDescription && (
                <div>
                  <p><strong>Customer Descripiton: </strong>{cakeDescription}</p>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Customer Instructions */}
          {order.customerNotes && (
            <div>
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Customer Instructions</h3>
              <p className="whitespace-pre-line">{order.customerNotes}</p>
            </div>
          )}



          {isQuoteOrder && !includeEmailMessageInPrint && emailMessage && (
            <div>
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Email Message to Customer</h3>
              <p className="whitespace-pre-line">{emailMessage}</p>
            </div>
          )}

          {isQuoteOrder && !includeAdditionalNotesInPrint && additionalNotes && (
            <div>
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Additional Notes</h3>
              <p className="whitespace-pre-line">{additionalNotes}</p>
              <p className="mt-1 text-xs italic text-gray-600">
                {customerCanSeeAdditional ? "Customer was allowed to view these notes." : "Customer could not see these notes."}
              </p>
            </div>
          )}



          {/* Attachments from Quote (if any) */}
          {isQuoteOrder && quoteFiles.length > 0 && (
            <div className="mt-3" style={{ breakInside: 'avoid' }}>
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Customer Attachments</h3>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {quoteFiles.map((f, idx) =>
                  f.mimeType?.startsWith('image/') ? (
                    <a key={idx} href={f.signedUrl} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={f.signedUrl}
                        alt={f.originalName}
                        crossOrigin="anonymous"
                        className="w-full max-h-40 object-contain rounded border"
                      />
                    </a>
                  ) : (
                    <a
                      key={idx}
                      href={f.signedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-candy-primary underline inline-flex items-center gap-1"
                    >
                      {f.originalName}
                    </a>
                  )
                )}
              </div>
            </div>
          )}

          {/* Pricing Breakdown (from accepted quote if available) */}
          {(isQuoteOrder && quotePrices) && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Pricing Breakdown</h3>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">Cake</div>
                  <div className="text-sm font-semibold">{fmtUSD(quotePrices.cake)}</div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">Decoration</div>
                  <div className="text-sm font-semibold">{fmtUSD(quotePrices.decoration)}</div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">Filling</div>
                  <div className="text-sm font-semibold">{fmtUSD(quotePrices.filling)}</div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">Flavor</div>
                  <div className="text-sm font-semibold">{fmtUSD(quotePrices.flavor)}</div>
                </div>
                {topperPrice > 0 && (
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Topper</div>
                    <div className="text-sm font-semibold">{fmtUSD(topperPrice)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Shop Notes in invoice (optional) */}
          {includeNotesInPrint && shopNotes?.trim() && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Shop Notes</h3>
              <p className="whitespace-pre-line">{shopNotes}</p>
            </div>
          )}

          {/* Totals */}
          <div className="pt-4 border-t flex justify-end text-base font-semibold">
            <p>Total: {fmtUSD(order.price)}</p>
          </div>
          {/* Payment Summary Below Total */}
          <div className="mt-2 flex justify-end text-sm">
            <div className="text-right">

              {/* Amount Paid */}
              <p>
                <strong>Paid:</strong> {fmtUSD(order.amountPaid || 0)}
              </p>

              {/* Remaining Balance */}
                <p>
                  <strong>Due:</strong> {fmtUSD(order.price - (order.amountPaid || 0))}
                </p>


            </div>
          </div>


          <div className="mt-10 text-center text-xs text-gray-600">
            <p>Thank you for your order!</p>
          </div>
        </div>


        {isQuoteOrder && (
          <div className="print:hidden w-full flex justify-center mt-4">
            <button
              onClick={() => setShowQuotes(!showQuotes)}
              className="px-3 py-[6px] text-sm border border-candy-primary text-candy-primary rounded-full hover:bg-candy-primary hover:text-white transition hide-on-print"
            >
              {showQuotes ? "Hide All Quotes" : "Expand All Quotes"}
            </button>
          </div>
        )}


        {showQuotes && (
          <div className="mt-4 space-y-4">
            {quotesList.map((q) => {
              const p = q.prices || {};
              const total =
                Number(p.cake || 0) +
                Number(p.decoration || 0) +
                Number(p.filling || 0) +
                Number(p.flavor || 0);

              return (
                <div
                  key={q._id}
                  className="border rounded-lg bg-white shadow-sm p-4 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-gray-700">
                      <strong>Quote ID:</strong> {q.quoteId}
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 border border-blue-200 text-blue-700">
                      {q.status}
                    </span>
                  </div>

                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="border px-2 py-1 bg-gray-50 text-center text-xs">
                      Cake: ${p.cake?.toFixed(2)}
                    </div>
                    <div className="border px-2 py-1 bg-gray-50 text-center text-xs">
                      Decoration: ${p.decoration?.toFixed(2)}
                    </div>
                    <div className="border px-2 py-1 bg-gray-50 text-center text-xs">
                      Filling: ${p.filling?.toFixed(2)}
                    </div>
                    <div className="border px-2 py-1 bg-gray-50 text-center text-xs">
                      Flavor: ${p.flavor?.toFixed(2)}
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-700">
                    <strong>Total:</strong> ${total.toFixed(2)}
                  </div>

                  {q.note && (
                    <p className="mt-2 text-xs italic text-gray-600">
                      <strong>Email Message:</strong> {q.note}
                    </p>
                  )}

                  {q.cakeData && (
                    <div className="mt-3">
                      <strong className="block text-xs text-gray-700">
                        Specs:
                      </strong>
                      <div className="flex flex-wrap gap-2 mt-1 text-xs">
                        {q.cakeData.size && (
                          <span className="px-2 py-1 bg-gray-100 rounded border">
                            Size: {q.cakeData.size}
                          </span>
                        )}
                        {q.cakeData.shape && (
                          <span className="px-2 py-1 bg-gray-100 rounded border">
                            Shape: {q.cakeData.shape}
                          </span>
                        )}
                        {q.cakeData.frosting && (
                          <span className="px-2 py-1 bg-gray-100 rounded border">
                            Frosting: {q.cakeData.frosting}
                          </span>
                        )}
                        {q.cakeData.topper && (
                          <span className="px-2 py-1 bg-gray-100 rounded border">
                            Topper: {q.cakeData.topper}
                          </span>
                        )}
                        {q.cakeData.additional && (
                          <span className="px-2 py-1 bg-gray-100 rounded border">
                            Notes: {q.cakeData.additional}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}



        {/* Shop Notes (editor) */}
        <div className="mb-6 print:hidden">
          <h3 className="text-lg font-semibold mb-1 text-candy-primary">Shop Notes</h3>
          <p className="text-xs text-gray-600 mb-2 leading-relaxed">
            Internal use only — not included in customer emails. Shop notes are not shared with the customer until you print an invoice with &ldquo;Include Shop Notes in invoice&rdquo; checked.
          </p>
          <textarea
            value={shopNotes}
            onChange={(e) => setShopNotes(e.target.value)}
            rows={4}
            className="w-full p-2 border border-gray-300 rounded text-sm"
            placeholder="Internal instructions, pickup reminders, etc."
          />
          <div className="flex items-center justify-between mt-2 text-sm">
            <div className="text-xs italic">
              {saving ? 'Saving...' : savedMessage ? <span className="text-green-600">{savedMessage}</span> : null}
            </div>
          </div>
        </div>

        {/* Invoice settings */}
        <div className="mb-6 print:hidden">
          <h3 className="text-lg font-semibold mb-1 text-candy-primary">Invoice Settings</h3>
          <div className="flex flex-col gap-2 text-gray-700 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeNotesInPrint}
                onChange={(e) => setIncludeNotesInPrint(e.target.checked)}
                className="accent-candy-primary"
              />
              <span>Include Shop Notes in invoice</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeDayColorInPrint}
                onChange={(e) => setIncludeDayColorInPrint(e.target.checked)}
                className="accent-candy-primary"
              />
              <span>Remove Weekday Color Block in invoice</span>
            </label>


            {isQuoteOrder && (<label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeEmailMessageInPrint}
                onChange={(e) => setIncludeEmailMessageInPrint(e.target.checked)}
                className="accent-candy-primary"
              />
              <span>Remove Email Message in invoice</span>
            </label>)}
            {isQuoteOrder && (<label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAdditionalNotesInPrint}
                onChange={(e) => setIncludeAdditionalNotesInPrint(e.target.checked)}
                className="accent-candy-primary"
              />
              <span>Remove Additional Notes in invoice</span>
            </label>)}


          </div>
        </div>

        <div className="mt-6">
          <OrderStatusPanel
            status={order.status}
            onAccept={handleAcceptClick}
            onReject={handleRejectClick}
            onStatusChange={handleUpdateStatus}
            onDelete={handleDeleteClick}
            canDelete={canDelete}
            busy={statusBusy || verifyOpen}
          />
        </div>

        {/* Verify Modal */}
        <div onClick={(e) => e.stopPropagation()}>
          <VerifyDialog
            open={verifyOpen}
            title={actionType === 'accept' ? 'Accept Order' : actionType === 'reject' ? 'Reject Order' : 'Delete Order'}
            message={`Are you sure you want to ${actionType} this order?`}
            warning={
              actionType === 'accept'
                ? '✅ Customer will be notified upon acceptance.'
                : actionType === 'reject'
                  ? '📧 The customer will be notified via email that their order could not be accepted.'
                  : actionType === 'delete'
                    ? '⚠️ Deleting this order is permanent and cannot be undone.'
                    : ''
            }
            onConfirm={handleVerifyConfirm}
            onCancel={handleVerifyCancel}
          >
            {actionType === 'reject' && (
              <div className="mb-4 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason shown in rejection email
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Add a short explanation for the customer..."
                />
              </div>
            )}
          </VerifyDialog>
        </div>
      </div>
    </div>
  );
};

export default OrderDialog;
